import { Fragment, useEffect, useMemo, useRef, useState } from "react";

import clsx from "clsx";
import { useOnClickOutside } from "usehooks-ts";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { type Level } from "@features/overview-v2/types";
import { breakdownComparator } from "@features/sales-v3/utils/breakdown-comparator";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Transition,
} from "@headlessui/react";
import { useFuzzySearch } from "@hooks/use-fuzzy-search";
import { renderChevronAnimation } from "@shared/selects/utils";

interface Props {
  disabled?: boolean;
  options: string[];
  value?: string;
  placeholder?: string;
  position?: number;
  showPosition?: boolean;
  maxAllowedLevelByBreakdown?: Record<string, Level>;
  onChange(breakdown: string): void;
  onDelete?(): void;
}

export const BreakdownSelect = ({
  disabled,
  options,
  value,
  placeholder,
  position,
  showPosition,
  maxAllowedLevelByBreakdown,
  onChange,
  onDelete,
}: Props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({
    id: value ?? "",
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [filteredOptions, { setSearch, clearSearch }] = useFuzzySearch(
    options,
    {
      searchKeys: [],
    },
  );

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      clearSearch();
    }
  }, [clearSearch, isOpen]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  useOnClickOutside(containerRef, () => setIsOpen(false));

  const sortedOptions = useMemo(
    () => [...filteredOptions].sort((a, b) => breakdownComparator(a, b)),
    [filteredOptions],
  );

  return (
    <div
      ref={(ref) => {
        containerRef.current = ref;
        setNodeRef(ref);
      }}
      className={clsx(
        "border rounded-md h-8 text-sm flex bg-v2-primary relative",
        {
          "z-[999] shadow-sm": isDragging,
          "border-cosmic ring-1 ring-inset ring-cosmic": isOpen,
          "border-v2-secondary hover:border-cosmic": !disabled,
          "border-v2-secondary": disabled,
        },
      )}
      style={style}
    >
      {showPosition && (
        <div
          ref={setActivatorNodeRef}
          className={clsx(
            "flex items-center justify-center w-8 rounded-l cursor-grab group/container",
            { "cursor-grabbing": isDragging },
          )}
          {...attributes}
          {...listeners}
        >
          <span
            className={clsx(
              "w-4 h-4 bg-cosmic text-xxxs text-white rounded-full",
              "flex items-center justify-center group-hover/container:hidden",
              { hidden: isDragging },
            )}
          >
            {!isSorting && position}
          </span>
          <i
            className={clsx(
              "fa-regular fa-sharp fa-grip-dots-vertical group-hover/container:block",
              { hidden: !isDragging, block: isDragging },
            )}
          ></i>
        </div>
      )}
      <div className={clsx("flex-1 h-full", { "cursor-pointer": !disabled })}>
        <Combobox
          disabled={disabled}
          value={value}
          onChange={(value) => {
            clearSearch();
            if (value) {
              onChange(value);
            }
          }}
        >
          <ComboboxButton
            as="div"
            className={clsx("flex items-center text-sm h-full pr-2.5", {
              "pl-2.5": !showPosition,
              "pl-1": showPosition,
            })}
            onClick={() => {
              setIsOpen(!isOpen);
            }}
          >
            {isOpen && (
              <ComboboxInput
                autoFocus
                className="flex-1 bg-transparent cursor-pointer"
                displayValue={() => ""}
                onChange={(e) => setSearch(e.target.value)}
              />
            )}
            {!isOpen && value && (
              <span className="flex-1 bg-transparent">
                {value}
                {maxAllowedLevelByBreakdown &&
                  maxAllowedLevelByBreakdown[value] &&
                  maxAllowedLevelByBreakdown[value] !== "campaign" && (
                    <span className="text-xxs text-cosmic ml-2">
                      {maxAllowedLevelByBreakdown[value] === "ad"
                        ? "Ad"
                        : "Adset, Ad"}
                    </span>
                  )}
              </span>
            )}
            {!isOpen && !value && (
              <span
                className={clsx("flex-1 bg-transparent tracking-wide", {
                  "text-disabled": !disabled,
                  "text-gray-1": disabled,
                })}
              >
                {placeholder ?? "Select..."}
              </span>
            )}
            <span className={clsx({ "text-disabled": disabled })}>
              {renderChevronAnimation(isOpen)}
            </span>
          </ComboboxButton>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
            show={isOpen}
          >
            <ComboboxOptions
              anchor={{
                to: "bottom start",
                gap: 14,
                offset: showPosition ? -37 : -11,
              }}
              className={clsx(
                "z-[9998] bg-v2-primary font-sans rounded-lg elevation-1",
                "px-0 py-1 max-h-[20rem] overflow-y-auto",
                {
                  "w-[calc(var(--button-width)+2px)]": !value && !showPosition,
                  "w-[calc(var(--button-width)+34px)]": value && !showPosition,
                  "w-[calc(var(--button-width)+66px)]": value && showPosition,
                },
              )}
            >
              {sortedOptions.length === 0 && (
                <div className="px-4 py-2 text-disabled">NO RESULTS FOUND</div>
              )}
              {sortedOptions.map((option) => (
                <ComboboxOption key={option} value={option} as={Fragment}>
                  {({ focus, selected }) => (
                    <div
                      className={clsx(
                        "px-4 h-9 cursor-pointer text-sm text-v2-primary font-normal",
                        "flex items-center",
                        {
                          "bg-cosmic-3 font-semibold": selected,
                          "bg-cosmic-4": focus,
                        },
                      )}
                    >
                      <div>
                        <div>{option}</div>
                        {maxAllowedLevelByBreakdown &&
                          maxAllowedLevelByBreakdown[option] &&
                          maxAllowedLevelByBreakdown[option] !== "campaign" && (
                            <div className="text-xxs text-cosmic font-normal">
                              {maxAllowedLevelByBreakdown[option] === "ad"
                                ? "Ad"
                                : "Adset, Ad"}
                            </div>
                          )}
                      </div>
                    </div>
                  )}
                </ComboboxOption>
              ))}
            </ComboboxOptions>
          </Transition>
        </Combobox>
      </div>
      {value && (
        <button
          className="w-8 border-l border-v2-secondary text-cosmic hover:text-mars"
          onClick={onDelete}
        >
          <i className={clsx("fa-regular fa-sharp fa-times-circle")}></i>
        </button>
      )}
    </div>
  );
};
